import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkle, TrendUp, Brain, Briefcase, UsersFour, MagnifyingGlass } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  "Generative AI": { icon: <Brain size={40} weight="duotone" />, color: "#5B8DEE" },
  "Technology": { icon: <Sparkle size={40} weight="duotone" />, color: "#00a3af" },
  "Stock Market": { icon: <TrendUp size={40} weight="duotone" />, color: "#34c759" },
  "Career": { icon: <Briefcase size={40} weight="duotone" />, color: "#FF9500" },
  "Professional Grooming": { icon: <UsersFour size={40} weight="duotone" />, color: "#AF52DE" }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredClasses, setFeaturedClasses] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [categoriesRes, featuredRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/masterclasses?featured=true&approved=true`)
      ]);
      setCategories(categoriesRes.data.categories);
      setFeaturedClasses(featuredRes.data.slice(0, 6));
    } catch {
      // silently handle fetch errors
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 gradient-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl font-bold tracking-tight text-white flex items-center gap-0"
          >
            <img src="/logo.png" alt="NeoMaestro" className="h-9 w-9 object-contain bg-white rounded-lg" />
            <span>NeoMaestro</span>
          </motion.div>
          <div className="hidden md:flex gap-6 items-center text-sm">
            <button onClick={() => navigate('/marketplace')} className="text-white/90 hover:text-white transition-colors">
              Browse Courses
            </button>
            <button onClick={() => navigate('/about')} className="text-white/90 hover:text-white transition-colors">About</button>
            <button className="text-white/90 hover:text-white transition-colors">For Trainers</button>
            <button className="text-white/90 hover:text-white transition-colors">Corporate</button>
            {user ? (
              <Button
                onClick={() => {
                  if (user.role === 'admin') navigate('/admin-dashboard');
                  else if (user.role === 'instructor') navigate('/instructor-dashboard');
                  else navigate('/marketplace');
                }}
                className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-6 font-medium"
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <button onClick={() => navigate('/auth')} className="text-white/90 hover:text-white transition-colors">Sign In</button>
                <Button
                  onClick={() => navigate('/auth')}
                  className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-6 font-medium"
                  data-testid="nav-auth-btn"
                >
                  Join Now
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-4 px-6 gradient-header">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-4"
            >
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-4 text-white" data-testid="hero-title">
                Find Your Perfect Masterclass
              </h1>
              <Button
                onClick={() => navigate('/auth?role=instructor')}
                className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-8 py-5 font-bold text-base mb-4"
                data-testid="list-masterclass-cta"
              >
                List your masterclass for free
              </Button>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-lg shadow-lg p-1.5 flex items-center gap-2">
                <MagnifyingGlass size={18} className="text-gray-400 ml-2" />
                <Input
                  placeholder="Search masterclasses..."
                  className="border-0 flex-1 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                  onKeyPress={(e) => e.key === 'Enter' && navigate('/marketplace')}
                />
                <Button
                  onClick={() => navigate('/marketplace')}
                  className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-lg px-4 py-2 font-medium text-sm"
                >
                  Search
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Masterclasses */}
      <section className="py-6 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-3xl font-bold text-gray-900">Featured Masterclasses</h2>
            <Button
              variant="ghost"
              onClick={() => navigate('/marketplace')}
              className="text-[#00a3af] hover:text-[#004d7a]"
            >
              See All
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {featuredClasses.slice(0, 4).map((mc, index) => (
              <motion.div
                key={mc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/masterclass/${mc.id}`)}
                className="card-elevated rounded-xl overflow-hidden cursor-pointer"
                data-testid={`featured-card-${index}`}
              >
                <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200">
                  {mc.image_url && (
                    <img src={mc.image_url} alt={mc.title} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-3 left-3">
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">LIVE</span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{mc.title}</h3>
                  <p className="text-sm text-gray-600 mb-3">👤 {mc.instructor_name}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">⏰ {mc.duration}</span>
                    <span className="font-bold text-[#00a3af]">{mc.price}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Categories</h2>
              <p className="text-gray-600">Learn essential skills in print, conceptual artboarding, and even streamlined UX methodologies</p>
            </div>
            <Button
              onClick={() => navigate('/marketplace')}
              className="bg-[#00a3af] text-white hover:bg-[#004d7a] rounded-full px-6 font-medium"
            >
              See All
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {categories.map((category) => {
              const { icon, color } = categoryIcons[category] || { icon: <Sparkle size={40} />, color: "#00a3af" };
              return (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  onClick={() => navigate(`/marketplace?category=${encodeURIComponent(category)}`)}
                  className="bg-white rounded-xl p-6 text-center cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`category-card-${category.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex justify-center mb-4" style={{ color }}>
                    {icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm">{category}</h3>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="gradient-footer text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="font-bold mb-4">Company</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Resources</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/marketplace')} className="hover:text-white transition-colors">Browse Courses</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Legal</h3>
              <ul className="space-y-2 text-white/80">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4">Socials</h3>
              <ul className="space-y-2 text-white/80">
                <li><button className="hover:text-white transition-colors">Post a Masterclass</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center">
            <Button
              onClick={() => navigate('/auth?role=instructor')}
              className="bg-[#34c759] text-white hover:bg-[#28a745] rounded-full px-8 py-3 font-medium mb-6"
            >
              List your masterclass for free
            </Button>
            <p className="text-white/70 text-sm">© 2026 NeoMaestro. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}